import { z } from 'zod';
import type { ScreenId } from './registry';

export const elementDetailParamSchema = z
  .string()
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

/**
 * Per-screen deeplink parameter schemas.
 * All param values MUST be z.string() — URL query params are always strings.
 * Features parse/coerce downstream from the validated string value.
 * Screens with no params declare z.object({}).
 */
export const screenParamsSchemas = {
  'updates/required': z.object({}),
  home: z.object({
    // Presence of `sheet=check-in` presents the daily check-in sheet over the
    // wayfinder, mirroring `login?sheet=sona-handoff`. Opaque slug only — no
    // PII travels in the URL. An unrecognised slug opens nothing.
    sheet: z.string().optional(),
    // Declared so the notification sweeper injects the launch handle here:
    // it reads this schema's shape rather than a screen allow-list, so a
    // destination targeting home carries the handle with no sweeper change.
    notificationIntentId: z.string().optional(),
  }),
  chat: z.object({
    accessToken: z.string().optional(),
    agentDefinitionId: z.string().optional(),
    agentFlowId: z.string().optional(),
    autoconnect: z.string().optional(),
    muteMic: z.string().optional(),
    debugText: z.string().optional(),
    // Dev/E2E only: seeds the progress-chart tool surface so the card can be
    // asserted without a live model deciding to call the tool.
    debugProgressChart: z.string().optional(),
    // Dev/E2E only, same purpose for the care-team booking card. A param absent
    // from this schema is stripped before the screen reads it, so omitting it
    // here silently disables the seed rather than failing — which is how the
    // booking card's E2E entry point sat broken while its atom reported PASS.
    debugCareTeamBooking: z.string().optional(),
    runId: z.string().optional(),
    // Opaque notification-launch handle carried from a push tap. The server
    // resolves it (ownership, status, freshness) and renders the resulting
    // context itself, so nothing about the notification travels in the URL
    // beyond this id. Declared here because an undeclared param is stripped
    // before the screen reads it — and because the notification destination
    // schema validates its params against this contract at write time.
    notificationIntentId: z.string().optional(),
  }),
  profile: z.object({
    tab: z.string().optional().meta({ previewShareable: true }),
  }),
  'profile-showcase': z.object({
    element: elementDetailParamSchema.optional().meta({ previewShareable: true }),
  }),
  'care-specialist': z.object({
    specialistId: z.string().optional().meta({ previewShareable: true }),
  }),
  'chat-choice': z.object({}),
  'guided-conversations-intro': z.object({}),
  'guided-conversations-select': z.object({}),
  'care-team-chat': z.object({
    conversationId: z.string().check(z.uuid()).optional().meta({ previewShareable: true }),
    // The notification-launch handle the sweeper injects into a push tap, so
    // the tap reaches the client open capture. Opaque; carries nothing about
    // the message.
    notificationIntentId: z.string().optional(),
  }),
  'care-outreach-queue': z.object({}),
  'escalation-review': z.object({}),
  'eligibility-review': z.object({}),
  'member-detail-changes': z.object({}),
  'care-upcoming-calls': z.object({}),
  'care-call-details': z.object({
    appointmentId: z.string().optional().meta({ previewShareable: true }),
  }),
  'care-previous-calls': z.object({}),
  'care-team-introduction': z.object({}),
  'care-first-call-booking': z.object({}),
  'hdhp-notice': z.object({}),
  'care-hdhp-call-booking': z.object({}),
  // The booking-outcome handoff: only the opaque appointment id crosses the
  // deeplink boundary (ADR-035 Rule 5). Display fields live in the in-memory
  // handoff set by the booking screen before navigation.
  'care-call-matched': z.object({
    appointmentId: z.string().optional().meta({ previewShareable: true }),
  }),
  medication: z.object({}),
  'medication-cravings': z.object({}),
  // The product form the page renders. Optional on the wire because a deeplink
  // may omit it; the screen narrows an unrecognised value against its closed
  // vocabulary rather than trusting it.
  'medication-cravings-product': z.object({
    product: z.string().optional(),
  }),
  greeting: z.object({}),
  'component-gallery': z.object({}),
  login: z.object({
    accessToken: z.string().optional(),
    refreshToken: z.string().optional(),
    previewRunKey: z.string().optional(),
    runId: z.string().optional(),
    // Presence of `sheet=sona-handoff` presents the Sona handoff bottom sheet
    // over the welcome screen. Opaque slug only — no PII travels in the URL.
    sheet: z.string().optional(),
    // `start=login` opens the email sign-in step directly — the migration
    // silent-handoff surface's fallback exit. Opaque slug only.
    start: z.string().optional(),
  }),
  download: z.object({}),
  signup: z.object({}),
  'signup/context': z.object({}),
  'signup/substance': z.object({}),
  'signup/legal-name': z.object({}),
  'signup/account': z.object({}),
  'signup/code': z.object({}),
  'signup/plan-details': z.object({}),
  'signup/eligibility-check': z.object({}),
  'signup/employer-search': z.object({}),
  'signup/address': z.object({}),
  'signup/birth-date': z.object({}),
  'signup/contact': z.object({}),
  'signup/quiz-motivation': z.object({}),
  'signup/quiz-goal': z.object({}),
  'signup/baseline-week': z.object({}),
  'signup/baseline-typical': z.object({}),
  'signup/baseline-adjust': z.object({}),
  'signup/screening': z.object({}),
  'signup/wrap-up': z.object({}),
  'signup/hdhp-notice': z.object({}),
  'signup/hdhp-booking': z.object({}),
  'signup/rewards-intro': z.object({}),
  'signup/rewards-points': z.object({}),
  'signup/call-choice': z.object({}),
  'signup/language-preference': z.object({}),
  'signup/booking': z.object({}),
  'signup/booking-unavailable': z.object({}),
  'signup/download': z.object({}),
  'signup/under-age': z.object({}),
  'signup/minor-unsupported': z.object({}),
  'signup/registered': z.object({}),
  'signup/error': z.object({}),
  'signup/book-a-call': z.object({}),
  'signup/login': z.object({}),
  'signup/login-code': z.object({}),
  'signup/login-no-progress': z.object({}),
  'signup/sso-contact': z.object({}),
  'signup/sso-code': z.object({}),
  'signup/terms-of-use': z.object({}),
  'signup/privacy-policy': z.object({}),
  'signup/notice-of-privacy-practices': z.object({}),
  'signup/consumer-health-data-privacy-notice': z.object({}),
  'signup/ai-notice': z.object({}),
  'signup/tcpa-authorization': z.object({}),
  'onboarding-start': z.object({}),
  'onboarding-name': z.object({}),
  'onboarding-motivation': z.object({}),
  'onboarding-substance': z.object({}),
  'onboarding-condition': z.object({}),
  'onboarding-goal': z.object({}),
  'onboarding-change-history': z.object({}),
  'onboarding-cutback-degree': z.object({}),
  'onboarding-sona-intro': z.object({}),
  'app-setup': z.object({}),
  'registered-summary': z.object({}),
  'onboarding-weekly-usage': z.object({}),
  'onboarding-week-summary': z.object({}),
  'onboarding-typical-usage': z.object({}),
  'onboarding-taps2': z.object({}),
  'onboarding-summary': z.object({}),
  'onboarding-rewards-intro': z.object({}),
  'onboarding-conversation-mode': z.object({}),
  'intake-agent': z.object({}),
  'intake-ai-consent': z.object({
    mode: z.string().optional(),
    returnTo: z.string().optional(),
    // The destination a Home-originated gate continues into after a grant:
    // opaque ids only, resolved server-side (ADR-015), never member identity.
    composedPracticeId: z.string().optional(),
    agentDefinitionId: z.string().optional(),
  }),
  'intake-withdrawal-check': z.object({}),
  // Which notice copy to show and which manual-path screen follows it. Closed
  // vocabularies parsed downstream; the member's state is never a param.
  'sona-state-notice': z.object({
    variant: z.string().optional(),
    next: z.string().optional(),
  }),
  'post-intake-feedback': z.object({}),
  'post-session-feedback': z.object({}),
  // Parameterless: the picker reads the member's quit date state and picks set or
  // change mode itself, so a link never carries a date.
  'quit-date-picker': z.object({}),
  // The catalogued module key and nothing else: the screen fetches the
  // module's served content itself, so a link never carries content.
  'cbt-module': z.object({ moduleKey: z.string().min(1).optional() }),
  'intake-plan': z.object({}),
  'initial-review-intro': z.object({}),
  'initial-review-activity': z.object({}),
  'initial-review-check-ins': z.object({}),
  'eligibility-confirm': z.object({}),
  'eligibility-employer-search': z.object({}),
  'eligibility-benefit-holder': z.object({}),
  'eligibility-name': z.object({}),
  'eligibility-contact': z.object({}),
  'eligibility-address': z.object({}),
  'eligibility-sex-at-birth': z.object({}),
  'eligibility-no-match': z.object({}),
  // The IdP's single-use `code` is read and stripped by the screen itself;
  // it is never a declared param, so no deeplink or log path carries it.
  saml: z.object({}),
  'saml-contact': z.object({}),
  'consent-pending-hold': z.object({}),
  'eligibility-under-age': z.object({}),
  'eligibility-minor-blocked': z.object({}),
  'assessment-showcase': z.object({
    step: z.string().optional(),
  }),
  'assessment-start': z.object({}),
  'assessment-phq2-q1': z.object({}),
  'assessment-phq2-q2': z.object({}),
  'assessment-gad2-q1': z.object({}),
  'assessment-gad2-q2': z.object({}),
  'assessment-phq9-q3': z.object({}),
  'assessment-phq9-q4': z.object({}),
  'assessment-phq9-q5': z.object({}),
  'assessment-phq9-q6': z.object({}),
  'assessment-phq9-q7': z.object({}),
  'assessment-phq9-q8': z.object({}),
  'assessment-phq9-q9': z.object({}),
  'assessment-gad7-q3': z.object({}),
  'assessment-gad7-q4': z.object({}),
  'assessment-gad7-q5': z.object({}),
  'assessment-gad7-q6': z.object({}),
  'assessment-gad7-q7': z.object({}),
  'assessment-session-suicide-screen': z.object({}),
  'assessment-complete': z.object({}),
  'assessment-result-no-mild': z.object({}),
  'assessment-result-moderate': z.object({}),
  'assessment-result-severe': z.object({}),
  'assessment-result-suicide-risk': z.object({}),
  rewards: z.object({}),
  'rewards-history': z.object({}),
  'points-history': z.object({}),
  'redemption-history': z.object({}),
  'gift-cards': z.object({}),
  'gift-card-redeem': z.object({
    brand: z.string().optional().meta({ previewShareable: true }),
  }),
  'gift-card-confirm': z.object({
    brand: z.string().optional().meta({ previewShareable: true }),
    points: z.string().optional().meta({ previewShareable: true }),
  }),
  'gift-card-success': z.object({
    status: z.string().optional().meta({ previewShareable: true }),
    id: z.string().optional().meta({ previewShareable: true }),
  }),
  'gift-items': z.object({}),
  'gift-item-detail': z.object({
    item: z.string().optional().meta({ previewShareable: true }),
  }),
  'gift-item-confirm': z.object({
    item: z.string().optional().meta({ previewShareable: true }),
  }),
  settings: z.object({}),
  'settings-notifications': z.object({}),
  'settings-my-data': z.object({}),
  'settings-my-data-privacy': z.object({}),
  'settings-legal': z.object({}),
  'settings-legal-privacy': z.object({}),
  'settings-legal-terms': z.object({}),
  'settings-legal-consumer-health': z.object({}),
  'settings-legal-privacy-practices': z.object({}),
  'weekly-plan-suggestions': z.object({
    mode: z
      .string()
      .regex(/^review-commit$/)
      .optional()
      .meta({ previewShareable: true }),
    reviewId: z.string().uuid().optional().meta({ previewShareable: true }),
    sourcePlanKind: z
      .string()
      .regex(/^(foundation|weekly)$/)
      .optional()
      .meta({ previewShareable: true }),
  }),
  'dev-developer-options': z.object({}),
  'dev-analytics-evidence': z.object({}),
  'dev-member-details': z.object({}),
  'dev-screen-preview': z.object({
    // 'preview', not 'screen' — React Navigation treats a 'screen' param as a
    // nested-navigation directive and swallows it before the route sees it.
    preview: z.string().optional(),
    component: z.string().optional(),
    fixture: z.string().optional(),
    // Internal automation inputs are schema-declared so cold-start held-intent
    // replay preserves the same route parameters as warm deep-link delivery.
    automation: z.string().optional(),
    accessToken: z.string().optional(),
    apiId: z.string().optional(),
    environmentKey: z.string().optional(),
    conversationId: z.string().optional(),
    participantId: z.string().optional(),
    graphqlUrl: z.string().optional(),
    realtimeUrl: z.string().optional(),
    triggerUrl: z.string().optional(),
    completionUrl: z.string().optional(),
  }),
  'dev-flag-override': z.object({
    flag: z.string().optional(),
    value: z.string().optional(),
    clear: z.string().optional(),
  }),
  'showcase-agent-launch': z.object({
    agentDefinitionId: z.string().optional(),
  }),
  'debug-network': z.object({
    fail: z.string().optional(),
  }),
  'telemedicine-call': z.object({
    appointmentId: z.string().optional().meta({ previewShareable: true }),
  }),
  // The post-call route carries the ended call's session id: the rating screen
  // submits feedback against it, and the summary route is handed it on the way
  // through. The rating and celebration routes also carry `appointmentId` — the
  // celebration reads the appointment's earned reward by it. Optional because the
  // screens tolerate absence — the rating screen degrades to the skip path and
  // the celebration skips itself rather than blocking.
  'telemedicine-call-rating': z.object({
    callSessionId: z.string().optional().meta({ previewShareable: true }),
    appointmentId: z.string().optional().meta({ previewShareable: true }),
  }),
  'telemedicine-call-summary': z.object({
    callSessionId: z.string().optional().meta({ previewShareable: true }),
  }),
  'telemedicine-call-celebration': z.object({
    callSessionId: z.string().optional().meta({ previewShareable: true }),
    appointmentId: z.string().optional().meta({ previewShareable: true }),
  }),
  'cab-onboarding': z.object({}),
  'sona-transfer': z.object({}),
  'migration-handoff': z.object({}),
  'migration-registration-consent': z.object({}),
  'migration-welcome': z.object({}),
  'migration-left-off': z.object({}),
  'migration-meet-sona': z.object({}),
  'migration-voice': z.object({}),
  'migration-ai-consent': z.object({}),
  'migration-preparing': z.object({}),
  'migration-text-preview': z.object({}),
  'migration-gap-program': z.object({}),
  'migration-gap-goal': z.object({}),
  'migration-gap-motivation': z.object({}),
  'cab-context-confirm': z.object({}),
  'auth-page': z.object({}),
  'members-list': z.object({}),
  'today-view': z.object({}),
  'member-details': z.object({}),
  // An opaque escalation id only: the route resolves it to a member server-side,
  // so the URL never pairs a member id with the fact of an escalation.
  'escalation-link': z.object({ escalationId: z.string().optional() }),
  'member-lookup': z.object({}),
  'clinicians-list': z.object({}),
  'clinician-details': z.object({}),
  'staff-profile': z.object({}),
  'call-staffing': z.object({}),
  'scheduler-links': z.object({}),
  'telemedicine-appointments': z.object({}),
  'bam-home': z.object({}),
  'bam-questionnaire': z.object({}),
  'not-found': z.object({}),
} as const satisfies Record<ScreenId, z.ZodObject<z.ZodRawShape>>;

export type ScreenParamsMap = {
  [K in ScreenId]: z.infer<(typeof screenParamsSchemas)[K]>;
};

/**
 * Deeplink params that carry credentials or other secrets and must never be
 * forwarded to analytics/logging sinks verbatim. Some screens schema-accept an
 * `accessToken` (the debug-login bridge), so a valid deeplink can legitimately
 * carry one — but the screen_view contract must not leak it downstream.
 */
const SENSITIVE_SCREEN_PARAM_KEYS = new Set<string>(['runId']);
const SENSITIVE_SCREEN_PARAM_PATTERN = /token|secret|password/i;
const REDACTED = '[redacted]';

/** True when a deeplink param key names a credential/secret value. */
export function isSensitiveScreenParamKey(key: string): boolean {
  return SENSITIVE_SCREEN_PARAM_KEYS.has(key) || SENSITIVE_SCREEN_PARAM_PATTERN.test(key);
}

/**
 * Return a copy of `params` with sensitive values replaced by a redaction
 * marker. Keys are preserved (so analytics can still see *that* a token-bearing
 * link was opened) while the values never reach the sink. Call this before any
 * screen_view / log emission that includes deeplink params.
 */
export function redactSensitiveScreenParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const redacted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    redacted[key] = isSensitiveScreenParamKey(key) ? REDACTED : value;
  }
  return redacted;
}

/** Shareable preview params opt in at their canonical field schema; undeclared/private values drop. */
export function parsePreviewScreenParams(
  screenId: ScreenId,
  params: Record<string, unknown>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, field] of Object.entries(screenParamsSchemas[screenId].shape)) {
    if (field.meta()?.previewShareable !== true || isSensitiveScreenParamKey(key)) continue;
    const parsed = field.safeParse(params[key]);
    if (parsed.success && typeof parsed.data === 'string') result[key] = parsed.data;
  }
  return result;
}
